import React, { useEffect, useMemo, useState } from "react";
import {
  DATE_RANGE_PRESETS,
  detectPresetFromRange,
  getPresetDateRange,
} from "../utils/rideReportDatePresets";
import { formatBreakdownDateRangeLabel } from "../utils/rideBreakdownComparison";

const inputClass =
  "w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white";

const toLocalDateString = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const DriverAnalyticsDateFilters = ({
  startDate = null,
  endDate = null,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [dateRangePreset, setDateRangePreset] = useState("");

  const startYmd = toLocalDateString(startDate);
  const endYmd = toLocalDateString(endDate);
  const hasMainDateRange = Boolean(startYmd && endYmd);

  useEffect(() => {
    setDateRangePreset(detectPresetFromRange(startYmd, endYmd));
  }, [startYmd, endYmd]);

  const activeRangeLabel = useMemo(() => {
    if (!hasMainDateRange) return "";
    if (dateRangePreset) {
      const preset = DATE_RANGE_PRESETS.find((p) => p.value === dateRangePreset);
      return preset?.label || formatBreakdownDateRangeLabel(startYmd, endYmd);
    }
    return formatBreakdownDateRangeLabel(startYmd, endYmd);
  }, [hasMainDateRange, dateRangePreset, startYmd, endYmd]);

  const handlePresetChange = (preset) => {
    setDateRangePreset(preset);
    if (!preset) return;
    const range = getPresetDateRange(preset);
    if (!range) return;
    onStartDateChange?.(range.startDate);
    onEndDateChange?.(range.endDate);
  };

  const handleStartChange = (value) => {
    setDateRangePreset("");
    onStartDateChange?.(value);
  };

  const handleEndChange = (value) => {
    setDateRangePreset("");
    onEndDateChange?.(value);
  };

  return (
    <div
      className={`rounded-lg border p-4 transition-colors duration-200 ${
        hasMainDateRange
          ? "bg-blue-50/50 border-blue-200 ring-1 ring-blue-200"
          : "bg-white/60 border-blue-100"
      }`}
    >
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-700">Date Range</h4>
        {hasMainDateRange && (
          <span className="inline-flex mt-1 items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            {activeRangeLabel}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="min-w-0">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Date Range
          </label>
          <select
            value={dateRangePreset}
            onChange={(e) => handlePresetChange(e.target.value)}
            className={inputClass}
          >
            {DATE_RANGE_PRESETS.map((preset) => (
              <option key={preset.value || "custom"} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-0">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Start Date
          </label>
          <input
            type="date"
            value={startYmd}
            onChange={(e) => handleStartChange(e.target.value)}
            max={toLocalDateString(new Date()) || undefined}
            className={inputClass}
          />
        </div>
        <div className="min-w-0">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            End Date
          </label>
          <input
            type="date"
            value={endYmd}
            onChange={(e) => handleEndChange(e.target.value)}
            max={toLocalDateString(new Date()) || undefined}
            min={startYmd || undefined}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );
};

export default DriverAnalyticsDateFilters;
